// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import {
  Tile,
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
} from '@carbon/react';
import { usePlanDetailContext } from './PlanDetailContext';
import { EmptyState } from '../../../components/shell/ScreenHelpers';

function formatNumber(value: number, decimals = 0): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: decimals });
}

function formatSolveStatus(status: string): string {
  return status
    .toLowerCase()
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatDeviceType(type: string): string {
  // "AirCooledChiller" → "Air Cooled Chiller"
  return type.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function PlanSavings() {
  const { planFile } = usePlanDetailContext();
  const rawInputs = planFile.raw_inputs;
  const solver = rawInputs?.solver_info;

  const deviceCounts = useMemo(() => {
    const devices = rawInputs?.optimization_result?.devices ?? [];
    const counts = new Map<string, number>();
    for (const d of devices) {
      counts.set(d.type, (counts.get(d.type) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rawInputs]);

  if (!solver) {
    return <EmptyState big="No optimization results are available for this plan." />;
  }

  const totalDevices = deviceCounts.reduce((sum, [, c]) => sum + c, 0);

  return (
    <div className="sched-stack">
      {/* Objective hero */}
      <Tile className="sched-hero">
        <span className="sched-hero-label">Total HVAC Energy</span>
        <span className="sched-hero-value">{formatNumber(solver.objective_value)} kWh</span>
      </Tile>

      {/* Solver summary */}
      <TableContainer title="Optimization Result">
        <Table size="lg">
          <TableHead>
            <TableRow>
              <TableHeader>Metric</TableHeader>
              <TableHeader>Value</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Objective (Total HVAC Energy)</TableCell>
              <TableCell>{formatNumber(solver.objective_value, 1)} kWh</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Solve Status</TableCell>
              <TableCell>{formatSolveStatus(solver.solve_status)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Solve Time</TableCell>
              <TableCell>{formatNumber(solver.solve_time_seconds, 2)} s</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Confidence</TableCell>
              <TableCell>{planFile.confidence}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Device inventory */}
      {deviceCounts.length > 0 && (
        <TableContainer title="Modeled Equipment">
          <Table size="lg">
            <TableHead>
              <TableRow>
                <TableHeader>Equipment Type</TableHeader>
                <TableHeader>Count</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {deviceCounts.map(([type, count]) => (
                <TableRow key={type}>
                  <TableCell>{formatDeviceType(type)}</TableCell>
                  <TableCell>{count}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell>
                  <strong>Total</strong>
                </TableCell>
                <TableCell>
                  <strong>{totalDevices}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}

export default PlanSavings;
